import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayerPosition, CardStack, CardType, StealableStack, GameMode } from '../../types';
import { CONSTANTS } from '../../data';
import Card from './Card';

const PlayerZone = ({
  position,
  name,
  score,
  cardStacks,
  isActive,
  cardBeingPlacedId,
  isBusting,
  bustingCards,
  stealableStacks,
  gameMode,
  bustAnimationDuration,
  speedLevel,
}: {
  position: PlayerPosition;
  name: string;
  score: number;
  cardStacks: CardStack[];
  isActive: boolean;
  cardBeingPlacedId: string | null;
  isBusting: boolean;
  bustingCards: CardType[];
  stealableStacks: StealableStack[];
  gameMode: GameMode;
  bustAnimationDuration: number;
  speedLevel: 1 | 2 | 4;
}) => {
  const positionClasses: { [key in PlayerPosition]: string } = {
    top: 'top-4 left-1/2 -translate-x-1/2 flex-col items-center',
    bottom: 'bottom-4 left-1/2 -translate-x-1/2 flex-col items-center',
    left: 'left-4 top-1/2 -translate-y-1/2 flex-row items-center',
    right: 'right-4 top-1/2 -translate-y-1/2 flex-row items-center',
  };

  const isVerticalLayout = position === 'left' || position === 'right';
  const isTopOrBottom = position === 'top' || position === 'bottom';
  const isCPU = gameMode === 'vs_cpu' && position !== 'bottom';

  const stealableStackIndices = useMemo(() => 
    new Set(
      stealableStacks
        .filter(s => s.player === position)
        .map(s => s.stackIndex)
    ), [stealableStacks, position]);

  const cardAnimationTransition = useMemo(() => (speedLevel > 1
    ? { type: 'tween', duration: CONSTANTS.SPEED_LEVELS[speedLevel].ANIMATION_DELAY / 1000 }
    : { type: 'spring', stiffness: 500, damping: 30 }
  ), [speedLevel]);

  return (
    <div className={`absolute flex gap-4 p-3 ${positionClasses[position]}`}>
      <motion.div 
        className={`p-4 rounded-2xl transition-all duration-300 backdrop-blur-sm border-2 shadow-2xl ${
          isBusting ? 'bg-gradient-to-br from-red-600/90 to-red-700/90 text-white animate-pulse border-red-400' : 
          isActive ? 'bg-gradient-to-br from-yellow-400/90 to-yellow-500/90 text-black border-yellow-300 shadow-yellow-400/50' : 
          'bg-gradient-to-br from-slate-800/90 to-slate-900/90 text-white border-slate-600'
        }`}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <div className={isTopOrBottom ? "flex items-baseline justify-center gap-4 mb-3" : "text-center mb-3"}>
          <h2 className={`font-bold text-xl tracking-wide ${!isTopOrBottom ? 'mb-1' : ''}`}>
            {name}
            {isCPU && <span className="text-xs font-normal bg-black/20 px-2 py-0.5 rounded-full ml-1 align-middle">CPU</span>}
          </h2>
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${
            isActive ? 'bg-black/20' : 'bg-white/20'
          }`}>
            <span>Score:</span>
            <span className="text-lg">{score}</span>
          </div>
        </div>
        <div className={`
          flex flex-wrap gap-2 mt-3
          ${isVerticalLayout 
            ? 'flex-col max-h-[35rem] w-auto items-center justify-start' 
            : 'flex-row max-w-xl justify-center items-start'
          }
        `}>
          <AnimatePresence>
            {cardStacks.map((stack, stackIndex) => {
              const isStealable = stealableStackIndices.has(stackIndex);
              return (
                <motion.div
                  layout
                  key={stack[0].number + '-' + stack[0].id}
                  className={`relative w-16 h-24 rounded-lg ${isStealable ? 'ring-4 ring-pink-500 animate-pulse shadow-pink-500/50' : ''}`}
                  exit={{ scale: 0, opacity: 0, transition: { duration: 0.5 } }}
                  whileHover={{ scale: 1.05 }}
                >
                  {stack.map((card, index) => (
                    <motion.div
                      key={card.id}
                      layoutId={card.id}
                      className="absolute"
                      style={{ zIndex: index }}
                      animate={{ x: index * 2, y: index * 2 }}
                      transition={cardAnimationTransition}
                    >
                      <Card card={card} />
                    </motion.div>
                  ))}
                  {stack.length > 1 && (
                    <motion.div 
                      className={`absolute -top-3 -right-3 text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center z-50 border-2 border-white shadow-lg ${
                        stack.length >= CONSTANTS.BUST_THRESHOLD ? 'bg-gradient-to-br from-red-600 to-red-700 text-white animate-pulse' : 'bg-gradient-to-br from-blue-600 to-blue-700 text-white'
                      }`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {stack.length}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default PlayerZone;
